import React, { CSSProperties, ReactElement, useState } from 'react';
import { Button, InfoButton, PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import styled from 'styled-components';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from './Styles';
import FluentModal from './FluentModal';

export const ConfirmationPromptTitle = styled.h1`
  font-size: 20px;
  font-weight: 500;
  line-height: 1.1;
  margin: 0;
  padding: 0 0 12px;
`;

/**
 * Wrap rendered result in a separate component so we can override it easily.
 * @deprecated - Use FluentDefaultConfirmationComponent.
 */
//Wrap rendered result in a separate component so we can override it easily.
const DefaultConfirmationComponent = ({
  title,
  message,
  buttonStyle,
  optionButtons,
}: {
  title: string;
  message: string;
  buttonStyle: CSSProperties;
  optionButtons: ReactElement | Array<ReactElement>;
}) => {
  return (
    <div>
      <div>
        <ConfirmationPromptTitle>{title}</ConfirmationPromptTitle>
        {message}
      </div>
      <div style={buttonStyle}>{optionButtons}</div>
    </div>
  );
};

/**
 * //TODO - Switch over to this component (Not yet/Not currently in use).
 * Intended new default for confirmation component.
 * However, since implementing components each have their own Modal wrapper, we can't directly apply without risking
 * breaking a bunch of components - So provided as a separate component for now.
 */
export const FluentDefaultConfirmationComponent = ({ title, message, buttonStyle, optionButtons }) => {
  return (
    <>
      <BasicHeader>{title}</BasicHeader>
      <div style={{ padding: '1em' }}>{message}</div>
      <ModalBottomButtonContainer style={buttonStyle}>{optionButtons}</ModalBottomButtonContainer>
    </>
  );
};

/**
 * A basic confirmation component for use when the UI required a confirmation without going via connected form saves.
 * @param isOpen - Whether the confirmation modal should currently be open.
 * @param title - Title of the confirmation
 * @param message - Message of the confirmation
 * @param onConfirm - When happens on confirmation - Expecting this to also set 'isOpen' to false.
 * @param onCancel - What happens on cancel - Expecting this to just set 'isOpen' to false.
 */
export const BasicConfirmation = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <FluentModal isOpen={isOpen} styles={{ main: { paddingBottom: '60px' } }}>
      <BasicHeader>{title}</BasicHeader>
      <div style={{ padding: '1em', whiteSpace: 'pre-wrap' }}>{message}</div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onCancel} data-testid={'confirmationPrompt-no'}>
          No
        </SecondaryButton>
        <PrimaryButton onClick={onConfirm} data-testid={'confirmationPrompt-yes'}>
          Yes
        </PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export const useConfirmationRequired = (onConfirm: () => any, title: string, message: string) => {
  const [open, setOpen] = useState<boolean>(false);

  const onClick = () => {
    setOpen(true);
  };

  const onConfirmSubmit = () => {
    setOpen(false);
    return onConfirm?.();
  };

  const confirmationModal = (
    <BasicConfirmation
      isOpen={open}
      title={title}
      message={message}
      onConfirm={onConfirmSubmit}
      onCancel={() => setOpen(false)}
    />
  );

  return {
    onClick,
    confirmationModal,
  };
};

/**
 * This confirmation implementation is outdated & not coherent with styles for Modals in P4.
 * A full replacement is yet to be developed, but for basic yes/no prompts "BasicConfirmation" above can be used.
 */
const DeprecatedConfirmationPrompt = ({
  confirmName = 'Yes',
  rejectName = 'No',
  onConfirmPromptResponse,
  onClose: propsOnClose,
  message,
  onReject,
  formData,
  confirm: propsConfirm,
  confirmationResponses,
  title: propsTitle,
  component: ConfirmationComponent = DefaultConfirmationComponent,
}: any) => {
  const buttonStyle: CSSProperties = {
    position: 'absolute',
    bottom: 16,
    right: 16,
    display: 'flex',
  };

  const confirm = () => propsConfirm(formData, propsOnClose);
  const onClose = () => onReject(formData, propsOnClose);

  const optionButtons: Array<JSX.Element> = [];
  let unique = 0;

  if (confirmationResponses && confirmationResponses.length) {
    confirmationResponses.forEach((response: any) => {
      let ConfirmationButton: any;
      switch (response.confirmationButtonType) {
        case 'SUCCESS':
          ConfirmationButton = PrimaryButton;
          break;
        case 'INFO':
          ConfirmationButton = InfoButton;
          break;
        case 'CANCEL':
        default:
          ConfirmationButton = Button;
      }

      let width = 80;
      if (response.name.length > 7) {
        width += (response.name.length - 7) * 7;
      }

      optionButtons.push(
        <ConfirmationButton
          key={response.name}
          data-testid={`confirmationPrompt${unique++}`}
          onClick={() => {
            if (response.confirmationButtonType === 'CANCEL') {
              //If we've declared this as a cancel button - Use cancel behaviour.
              onClose();
              return;
            }
            onConfirmPromptResponse(response.value);
          }}
          minWidth={`${width}px`}
        >
          {response.name}
        </ConfirmationButton>
      );
    });
  } else {
    optionButtons.push(
      <Button key={rejectName} data-testid="confirmationPrompt-no" onClick={onClose} minWidth="80px">
        {rejectName}
      </Button>
    );

    optionButtons.push(
      <PrimaryButton key={confirmName} data-testid="confirmationPrompt-yes" onClick={confirm} minWidth="80px">
        {confirmName}
      </PrimaryButton>
    );
  }

  const unescapedMessage =
    message &&
    message
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#x27;/g, "'")
      .replace(/&#x60;/g, '`');

  let title = propsTitle;
  if (typeof title === 'string') {
    title = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
  }

  return (
    <ConfirmationComponent
      title={title}
      message={unescapedMessage}
      buttonStyle={buttonStyle}
      optionButtons={optionButtons}
    />
  );
};

export default DeprecatedConfirmationPrompt;
